import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { TextField, Button, Typography, Box, Alert, Link, CircularProgress } from '@mui/material';
import axios from 'axios';

export default function ResetPassword() {
  // Get the token from the URL query parameters (e.g., ?token=...)
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // State for the form inputs
  const [identifier, setIdentifier] = useState(''); // For email/username
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // State for UI feedback
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // This effect clears the feedback message when the user switches between modes
  useEffect(() => {
    setFeedback({ type: '', message: '' });
  }, [token]);

  // --- HANDLERS ---

  // Handles the initial request to send a reset email
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFeedback({ type: '', message: '' });

    try {
      const res = await axios.post('/authapi/request_password_reset.php', { identifier });
      setFeedback({ type: 'success', message: res.data.message });
      setIsSubmitted(true); // Prevents re-submission
    } catch (err) {
      // The backend always returns success, so this catch is for network/server errors
      setFeedback({ type: 'error', message: 'A server error occurred. Please try again later.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handles the final submission of the new password
  const handleConfirmReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setFeedback({ type: 'error', message: 'Passwords do not match.' });
      return;
    }
    if (newPassword.length < 8) {
      setFeedback({ type: 'error', message: 'Password must be at least 8 characters long.' });
      return;
    }

    setIsLoading(true);
    setFeedback({ type: '', message: '' });

    try {
      const res = await axios.post('/authapi/confirm_password_reset.php', {
        token,
        new_password: newPassword
      });
      setFeedback({ type: 'success', message: res.data.message });
      setIsSubmitted(true); // Prevents re-submission and hides the form
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'An unexpected error occurred.' });
    } finally {
      setIsLoading(false);
    }
  };

  // --- RENDER LOGIC ---

  // If a token is present in the URL, render the "Set New Password" form
  if (token) {
    return (
      <Box sx={{ maxWidth: 450, mx: 'auto', mt: 8, p: 3, boxShadow: 3, borderRadius: 2 }}>
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Set a New Password
        </Typography>
        
        {isSubmitted && feedback.type === 'success' ? (
          <>
            <Alert severity="success">{feedback.message}</Alert>
            <Button component={RouterLink} to="/login" variant="contained" fullWidth sx={{ mt: 2 }}>
              Back to Login
            </Button>
          </>
        ) : (
          <Box component="form" onSubmit={handleConfirmReset}>
            <TextField
              fullWidth
              required
              label="New Password"
              type="password"
              margin="normal"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              fullWidth
              required
              label="Confirm New Password"
              type="password"
              margin="normal"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              disabled={isLoading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Update Password'}
            </Button>
            {feedback.message && <Alert severity={feedback.type || 'info'}>{feedback.message}</Alert>}
          </Box>
        )}
      </Box>
    );
  }

  // Otherwise, render the "Forgot Password" form
  return (
    <Box sx={{ maxWidth: 450, mx: 'auto', mt: 8, p: 3, boxShadow: 3, borderRadius: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom align="center">
        Forgot Your Password?
      </Typography>
      
      {isSubmitted ? (
        <Alert severity="success">{feedback.message}</Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No problem. Enter your username or email address below and we'll send you a link to reset it.
          </Typography>
          <Box component="form" onSubmit={handleRequestReset}>
            <TextField
              fullWidth
              required
              label="Username or Email"
              margin="normal"
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              disabled={isLoading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Send Reset Link'}
            </Button>
            {feedback.message && <Alert severity={feedback.type || 'info'}>{feedback.message}</Alert>}
          </Box>
        </>
      )}
       <Typography variant="body2" align="center" sx={{ mt: 3 }}>
        <Link component={RouterLink} to="/login">
            Remembered your password? Back to Login
        </Link>
       </Typography>
    </Box>
  );
}